// API client for health tracking endpoints
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiConfig } from './config';
import {
  EventsResponse,
  TraceResponse,
  StatsResponse,
  CreateEventRequest,
  CreateEventResponse,
  DailySummary,
  UserProfile,
  UpdateUserProfileRequest,
  UpdateUserContextResponse,
  HomeContext,
  LogEntry,
  KnowledgeItem,
  KnowledgeListResponse,
} from './types';

class HealthAPI {
  private baseUrl: string;

  constructor() {
    this.baseUrl = apiConfig.baseUrl;
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();



      return {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      };
    } catch (error) {
      console.error('Error getting auth session:', error);
      return {
        'Content-Type': 'application/json',
      };
    }
  }

  async getEvents(params?: {
    start?: string;
    end?: string;
    type?: 'bathroom' | 'meal' | 'weight' | 'water' | 'blood_pressure';
    limit?: number;
    exclude_types?: string[];
  }): Promise<EventsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.start) queryParams.append('start', params.start);
    if (params?.end) queryParams.append('end', params.end);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.exclude_types) {
      params.exclude_types.forEach(t => queryParams.append('exclude_types', t));
    }

    const url = `${this.baseUrl}${apiConfig.endpoints.events}${queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;

    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch events: ${response.statusText}`);
    }

    const data = await response.json();
    // Map span_type to type for frontend compatibility
    if (data.events) {
      data.events = data.events.map((e: any) => ({
        ...e,
        type: e.span_type || e.type
      }));
    }

    return data;
  }

  async createEvent(event: CreateEventRequest): Promise<CreateEventResponse> {
    const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.events}`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `Failed to create event: ${response.statusText}`);
    }

    return response.json();
  }

  async getTrace(traceId: string): Promise<TraceResponse> {
    const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.trace}/${traceId}`, {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch trace: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Map span_type to type for frontend compatibility
    if (data.primary_span) {
      data.primary_span.type = data.primary_span.span_type || data.primary_span.type;
    }
    
    if (data.linked_meals) {
      data.linked_meals = data.linked_meals.map((meal: any) => ({
        ...meal,
        type: meal.span_type || meal.type
      }));
    }

    return data;
  }

  async updateEvent(
    traceId: string,
    updates: {
      metadata?: Partial<any>;
      sub_context?: string;
    }
  ): Promise<{
    message: string;
    trace_id: string;
    updated_fields: string[];
    sub_context_updated: boolean;
    re_analysis_triggered: boolean;
  }> {
    const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.events}/${traceId}`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `Failed to update event: ${response.statusText}`);
    }

    return response.json();
  }

  async deleteEvent(traceId: string): Promise<{ message: string; trace_id: string }> {
    const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.events}/${traceId}`, {
      method: 'DELETE',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `Failed to delete event: ${response.statusText}`);
    }

    return response.json();
  }

  async getStats(period: 'day' | 'week' | 'month' = 'week'): Promise<StatsResponse> {
    const response = await fetch(
      `${this.baseUrl}${apiConfig.endpoints.stats}?period=${period}`,
      {
        method: 'GET',
        headers: await this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch stats: ${response.statusText}`);
    }

    return response.json();
  }

  async analyzeEvent(traceId: string): Promise<{ trace_id: string; ai_observation: string }> {
    const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.analyzeEvent}`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ trace_id: traceId }),
    });

    if (!response.ok) {
      throw new Error(`Failed to analyze event: ${response.statusText}`);
    }

    return response.json();
  }

  async getDailySummary(date?: string, forceRefresh: boolean = false): Promise<DailySummary> {
    const queryParams = new URLSearchParams();
    if (date) queryParams.append('date', date);
    if (forceRefresh) queryParams.append('force', 'true');

    const url = `${this.baseUrl}${apiConfig.endpoints.dailySummary}${queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;

    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch daily summary: ${response.statusText}`);
    }

    return response.json();
  }

  async updateDailySummary(
    date: string,
    sub_context: string
  ): Promise<{
    message: string;
    date: string;
    sub_context_updated: boolean;
    re_analysis_triggered: boolean;
  }> {
    const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.dailySummary}`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ date, sub_context }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `Failed to update daily summary: ${response.statusText}`);
    }

    return response.json();
  }

  async getUserProfile(): Promise<UserProfile> {
    const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.userContext}`, {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user profile: ${response.statusText}`);
    }

    return response.json();
  }

  async updateUserProfile(
    request: UpdateUserProfileRequest
  ): Promise<UpdateUserContextResponse> {
    const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.userContext}`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `Failed to update user context: ${response.statusText}`);
    }

    return response.json();
  }

  async getHomeContext(force: boolean = false): Promise<HomeContext> {
    const queryParams = new URLSearchParams();
    if (force) queryParams.append('force', 'true');

    const url = `${this.baseUrl}/home-context${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      // Handle rate limit error
      if (response.status === 429) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Rate limit exceeded');
      }
      throw new Error(`Failed to fetch home context: ${response.statusText}`);
    }

    return response.json();
  }

  async chatWithAssistant(message: string): Promise<{ response: string }> {
    const response = await fetch(`${this.baseUrl}/chat`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ message }),
    });

    if (!response.ok) {
      throw new Error(`Failed to chat with assistant: ${response.statusText}`);
    }

    return response.json();
  }
  async getOperationalData(): Promise<{ logs: LogEntry[] }> {
    const response = await fetch(`${this.baseUrl}/operational-data`, {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch operational data: ${response.statusText}`);
    }

    return response.json();
  }

  async getKnowledge(): Promise<KnowledgeListResponse> {
    const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.knowledge}`, {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch knowledge: ${response.statusText}`);
    }

    return response.json();
  }

  async getKnowledgeItem(topic: string): Promise<KnowledgeItem> {
    const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.knowledge}/${topic}`, {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch knowledge item: ${response.statusText}`);
    }

    return response.json();
  }

  async updateKnowledgeItem(topic: string, content: string): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.knowledge}/${topic}`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ content }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update knowledge item: ${response.statusText}`);
    }

    return response.json();
  }

  async researchTopic(topic: string): Promise<{ topic: string; summary: string }> {
    const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.research}`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ topic }),
    });

    if (!response.ok) {
      throw new Error(`Failed to research topic: ${response.statusText}`);
    }

    return response.json();
  }
}

export const healthAPI = new HealthAPI();
